import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    orderType: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! Your message has been sent. We'll get back to you soon!");
      setFormData({
        name: '',
        email: '',
        phone: '',
        orderType: 'general',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Sorry, we could not send your message. Please email us directly at TasteTPistachio@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="contact-section py-20 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <span className="section-label">Get in Touch</span>
              <h2 className="section-title mt-3 mb-4">Let's Create Something
                <span className="block text-gold">Delicious Together</span>
              </h2>
              <p className="text-lg text-olive-dark">
                Ready to order or have questions? We'd love to hear from you! Whether it's a custom cake 
                for a special occasion or just a quick question, reach out anytime.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <div className="contact-info-item">
                <MapPin className="text-gold" size={24} />
                <div>
                  <p className="font-semibold text-olive">Location</p>
                  <p className="text-olive-dark">Clermont, Florida</p>
                </div>
              </div>
              <div className="contact-info-item">
                <Mail className="text-gold" size={24} />
                <div>
                  <p className="font-semibold text-olive">Email</p>
                  <p className="text-olive-dark">TasteTPistachio@gmail.com</p>
                </div>
              </div>
              <div className="contact-info-item">
                <Phone className="text-gold" size={24} />
                <div>
                  <p className="font-semibold text-olive">Phone</p>
                  <p className="text-olive-dark">352-901-3169</p>
                </div>
              </div>
            </div>

            {/* Custom Order Info */}
            <div className="custom-order-info">
              <h3 className="text-xl font-bold text-olive mb-3">Custom Order Guidelines</h3>
              <ul className="space-y-2 text-olive-dark">
                <li className="flex items-start">
                  <span className="text-gold mr-2">•</span>
                  <span>Please order at least 3-5 days in advance for custom cakes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-2">•</span>
                  <span>Minimum order quantities may apply for certain items</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-2">•</span>
                  <span>All ingredients are all-natural and organic</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-2">•</span>
                  <span>Local pickup available in Clermont, FL area</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="form-label">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="form-label">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="orderType" className="form-label">Order Type *</label>
                <select
                  id="orderType"
                  name="orderType"
                  value={formData.orderType}
                  onChange={handleChange}
                  required
                  className="form-input"
                >
                  <option value="general">General Inquiry</option>
                  <option value="custom-cake">Custom Cake Order</option>
                  <option value="cupcakes">Cupcakes</option>
                  <option value="cookies">Cookies</option>
                  <option value="specialty">Specialty Items</option>
                  <option value="bulk">Bulk Order</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="form-label">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="form-input resize-none"
                  placeholder="Tell us about your order or ask us anything..."
                ></textarea>
              </div>

              <button type="submit" className="form-submit-button" disabled={isSubmitting}>
                <Send size={20} />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
